/**
 * Edit relative-permeability on fixed Sw grid 0…1 (step 0.1).
 * Choose a lithology shape preset or edit manually.
 * On save: if endpoints disagree with So / E_d → cancel or save & update properties.
 */
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { CommitNumberField } from '@/components/Scenarios/CommitNumberField';
import {
  densifyRelPermForChart,
  findRelPermPropsConflicts,
  maskZeroKrForChart,
  RELPERM_PRESETS,
  type RelPermPresetId,
  type RelPermPropsConflict,
  relPermChartMeta,
  relPermPtsFromPreset,
  relPermPtsToTable,
  resampleToSwGrid,
  type RelPermPoint,
} from '@/utils/materialBalanceRelperm';

export type RelPermPropsPatch = { so?: number; kvyt?: number };

export interface RelPermEditDialogProps {
  open: boolean;
  layerName: string;
  /** Points currently shown on the main chart (Corey or table) */
  sourcePts: RelPermPoint[];
  /** Current Properties So (fraction) — used for presets + consistency check */
  so: number;
  /** Current Properties E_d / kvyt (fraction) */
  kvyt: number;
  readOnly?: boolean;
  saving?: boolean;
  onClose: () => void;
  /**
   * Persist edited table; optional propsPatch updates So / E_d when the user
   * chooses “save and adjust reservoir properties”.
   */
  onSave: (table: number[][], propsPatch?: RelPermPropsPatch) => void | Promise<void>;
}

function rowsEqual(a: RelPermPoint[], b: RelPermPoint[]): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (r, i) => r.sw === b[i].sw && r.krw === b[i].krw && r.kro === b[i].kro,
  );
}

function propsPatchFromConflicts(conflicts: RelPermPropsConflict[]): RelPermPropsPatch {
  const patch: RelPermPropsPatch = {};
  for (const c of conflicts) {
    if (c.field === 'so') patch.so = c.implied;
    if (c.field === 'kvyt') patch.kvyt = c.implied;
  }
  return patch;
}

export const RelPermEditDialog: React.FC<RelPermEditDialogProps> = ({
  open,
  layerName,
  sourcePts,
  so,
  kvyt,
  readOnly = false,
  saving = false,
  onClose,
  onSave,
}) => {
  const [baseline, setBaseline] = useState<RelPermPoint[]>([]);
  const [rows, setRows] = useState<RelPermPoint[]>([]);
  const [pattern, setPattern] = useState<'manual' | RelPermPresetId>('manual');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [conflictOpen, setConflictOpen] = useState(false);
  const [pendingConflicts, setPendingConflicts] = useState<RelPermPropsConflict[]>([]);

  useEffect(() => {
    if (!open) return;
    const initial = resampleToSwGrid(sourcePts);
    setBaseline(initial);
    setRows(initial.map((r) => ({ ...r })));
    setPattern('manual');
    setConfirmOpen(false);
    setConflictOpen(false);
    setPendingConflicts([]);
  }, [open, sourcePts]);

  const dirty = useMemo(() => !rowsEqual(rows, baseline), [rows, baseline]);
  const chartMeta = useMemo(() => relPermChartMeta(rows), [rows]);
  const chartData = useMemo(
    () => maskZeroKrForChart(densifyRelPermForChart(rows)),
    [rows],
  );

  const applyPattern = (id: RelPermPresetId) => {
    const next = relPermPtsFromPreset(id, so, kvyt);
    setRows(next.map((r) => ({ ...r })));
    setPattern(id);
  };

  const commitKr = (index: number, key: 'krw' | 'kro', n: number | null) => {
    setPattern('manual');
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== index) return r;
        const next = n == null ? 0 : Math.max(0, n);
        return { ...r, [key]: Number(next.toFixed(3)) };
      }),
    );
  };

  const requestClose = () => {
    if (readOnly || !dirty) {
      onClose();
      return;
    }
    setConfirmOpen(true);
  };

  const discardAndClose = () => {
    setConfirmOpen(false);
    setConflictOpen(false);
    setPendingConflicts([]);
    setRows(baseline.map((r) => ({ ...r })));
    onClose();
  };

  const trySave = () => {
    const conflicts = findRelPermPropsConflicts(rows, so, kvyt);
    if (conflicts.length > 0) {
      setPendingConflicts(conflicts);
      setConfirmOpen(false);
      setConflictOpen(true);
      return;
    }
    void finishSave();
  };

  const finishSave = async (propsPatch?: RelPermPropsPatch) => {
    setConfirmOpen(false);
    setConflictOpen(false);
    setPendingConflicts([]);
    const table = relPermPtsToTable(rows);
    await onSave(table, propsPatch);
    onClose();
  };

  const selectedPreset = RELPERM_PRESETS.find((p) => p.id === pattern);

  return (
    <>
      <Dialog
        open={open}
        onClose={() => requestClose()}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          Relative permeability — {layerName}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Choose a typical shape for the lithology, or edit krw/kro manually. Sw is fixed from 0
            to 1 (step 0.1). Endpoints must stay consistent with Properties So and E_d (within one
            Sw step).
          </Typography>

          <FormControl size="small" sx={{ mb: 1.5, minWidth: 280 }} disabled={readOnly || saving}>
            <InputLabel id="relperm-pattern-label">Pattern</InputLabel>
            <Select
              labelId="relperm-pattern-label"
              label="Pattern"
              value={pattern}
              onChange={(e) => {
                const v = e.target.value as 'manual' | RelPermPresetId;
                if (v === 'manual') {
                  setPattern('manual');
                  return;
                }
                applyPattern(v);
              }}
            >
              <MenuItem value="manual">Manual</MenuItem>
              {RELPERM_PRESETS.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedPreset ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, mt: -1 }}>
              {selectedPreset.description} Endpoints from current So={so.toFixed(3)}, E_d=
              {kvyt.toFixed(3)}.
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, mt: -1 }}>
              Manual table — zeros stay stored but are not drawn.
            </Typography>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
              alignItems: 'stretch',
              minHeight: 360,
            }}
          >
            <TableContainer
              sx={{
                maxHeight: 420,
                overflow: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Sw</TableCell>
                    <TableCell>krw</TableCell>
                    <TableCell>kro</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={r.sw}>
                      <TableCell sx={{ py: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                        {r.sw.toFixed(1)}
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        <CommitNumberField
                          size="small"
                          value={r.krw}
                          allowEmpty
                          disabled={readOnly || saving}
                          formatCommitted={(n) => Number(Math.max(0, n).toFixed(3))}
                          onCommit={(n) => commitKr(i, 'krw', n)}
                          inputProps={{ step: 0.001, min: 0 }}
                          sx={{ width: 96 }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        <CommitNumberField
                          size="small"
                          value={r.kro}
                          allowEmpty
                          disabled={readOnly || saving}
                          formatCommitted={(n) => Number(Math.max(0, n).toFixed(3))}
                          onCommit={(n) => commitKr(i, 'kro', n)}
                          inputProps={{ step: 0.001, min: 0 }}
                          sx={{ width: 96 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                minWidth: 0,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 0.5,
                height: { xs: 280, sm: '100%' },
                minHeight: 280,
                bgcolor: 'background.paper',
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 8, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.4} />
                  <XAxis
                    dataKey="sw"
                    type="number"
                    domain={chartMeta.xDomain}
                    allowDataOverflow
                    tickCount={5}
                    tick={{ fontSize: 10 }}
                    label={{ value: 'Sw', position: 'insideBottom', offset: -2, fontSize: 10 }}
                  />
                  <YAxis
                    type="number"
                    domain={chartMeta.yDomain}
                    allowDataOverflow
                    tickCount={5}
                    tick={{ fontSize: 10 }}
                    width={42}
                    tickFormatter={(v) =>
                      Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })
                    }
                  />
                  <Tooltip
                    formatter={(value) =>
                      typeof value === 'number' ? value.toFixed(3) : String(value ?? '')
                    }
                    labelFormatter={(label) => `Sw = ${Number(label).toFixed(1)}`}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <ReferenceLine
                    x={chartMeta.swi}
                    stroke="#94a3b8"
                    strokeDasharray="3 3"
                    ifOverflow="extendDomain"
                  />
                  <ReferenceLine
                    x={1 - chartMeta.sor}
                    stroke="#94a3b8"
                    strokeDasharray="3 3"
                    ifOverflow="extendDomain"
                  />
                  <Line
                    type="monotone"
                    dataKey="kro"
                    name="kro"
                    stroke="#334155"
                    strokeWidth={2}
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="krw"
                    name="krw"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={requestClose} disabled={saving}>
            Close
          </Button>
          {!readOnly && (
            <Button
              variant="contained"
              disabled={saving || !dirty}
              onClick={() => trySave()}
            >
              {saving ? 'Saving…' : 'Save & close'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Save relative permeability?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Keep the edited kr table for this reservoir? Choosing No discards changes and restores
            the previous curve.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={discardAndClose} disabled={saving}>
            No, discard
          </Button>
          <Button variant="contained" onClick={() => trySave()} disabled={saving}>
            Yes, save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={conflictOpen} onClose={() => setConflictOpen(false)}>
        <DialogTitle>Curve does not match reservoir properties</DialogTitle>
        <DialogContent>
          <DialogContentText component="div" sx={{ mb: 1 }}>
            Relative-permeability endpoints imply different So / E_d than Properties:
          </DialogContentText>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {pendingConflicts.map((c) => (
              <Typography key={c.field} component="li" variant="body2" sx={{ mb: 0.5 }}>
                {c.label}: Properties {c.current.toFixed(3)} → curve implies {c.implied.toFixed(3)}
              </Typography>
            ))}
          </Box>
          <DialogContentText sx={{ mt: 1.5 }}>
            Cancel to keep editing, or save the curve and update Properties to match.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConflictOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={() => void finishSave(propsPatchFromConflicts(pendingConflicts))}
          >
            Save & update properties
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
